/**
 * Block 12 PR-B Phase B3 Part C Step 22 — SectionServices (Ж).
 *
 * 6 fixed rows per методика §1.4.3 (см. SERVICE_ROW_SPECS in Step 14):
 *   3 auto-bind: Распил ЛДСП (corpus.qty), Распил ХДФ (hdf.qty), Кромление (corpus.qty)
 *   3 manual:    Склейка, Услуги газели, Услуги грузчиков
 *
 * Toggleable per row. Auto-bind rows показывают qtyAuto + ↻ reset-to-auto button
 * через `<ResultRow>` (Step 15). Manual rows имеют qtyAuto=0 (user вводит qty).
 *
 * Reference: ADR-0020 §Q5, методика §1.4.3 OQ-8/10/12.
 */

import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import { buildServiceRow, SERVICE_ROW_SPECS } from '../helpers/computeAutoServiceQty';
import { ResultRow } from './ResultRow';
import type { MaterialRow } from '../types';

export interface SectionServicesProps {
  materialRows: MaterialRow[];
  toggles: Record<string, boolean>;
  overrides: Record<string, number>;
  prices: Record<string, number>;
  onToggle: (id: string, on: boolean) => void;
  onOverride: (id: string, qty: number) => void;
  onResetAuto: (id: string) => void;
  onPrice: (id: string, price: number) => void;
  /** PR-calc-buttons-unify (ADR-0099) — жёлтая кнопка «Найти и узнать цену» → /partners. */
  onDeeplink?: (rowId: string) => void | Promise<void>;
}

export function SectionServices({
  materialRows,
  toggles,
  overrides,
  prices,
  onToggle,
  onOverride,
  onResetAuto,
  onPrice,
  onDeeplink,
}: SectionServicesProps) {
  return (
    <SectionCard testId="section-services">
      <SectionHeader>Услуги</SectionHeader>
      <div className="flex flex-col gap-3">
        {SERVICE_ROW_SPECS.map((spec) => {
          const enabled = Boolean(toggles[spec.id]);
          if (!enabled) {
            return (
              <button
                key={spec.id}
                type="button"
                onClick={() => onToggle(spec.id, true)}
                data-testid={`service-add-${spec.id}`}
                className="min-h-tap rounded-xl border-[1.5px] border-dashed border-accent bg-card text-ink font-semibold text-base px-4 text-left focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
              >
                + Добавить «{spec.name}»
                {spec.autoBindKey ? <span className="text-muted text-sm ml-2">(auto)</span> : null}
              </button>
            );
          }
          const serviceRow = buildServiceRow({
            spec,
            materialRows,
            serviceOverrides: overrides,
            servicePrices: prices,
          });
          // PR-calc-buttons-unify (ADR-0099) — inline deeplink, чтобы показать
          // жёлтую «Найти и узнать цену» → /partners. НЕ меняем SERVICE_ROW_SPECS /
          // buildServiceRow (инфраструктура расчётов, КП integration).
          const serviceRowWithDeeplink = { ...serviceRow, deeplink: 'partner' as const };
          return (
            <div key={spec.id} className="flex flex-col gap-2" data-testid={`service-${spec.id}`}>
              <ResultRow
                row={serviceRowWithDeeplink}
                onQtyChange={(q) => onOverride(spec.id, q)}
                onResetAuto={spec.autoBindKey ? () => onResetAuto(spec.id) : undefined}
                onPriceChange={(p) => onPrice(spec.id, p)}
                onDeeplink={onDeeplink ? () => onDeeplink(spec.id) : undefined}
              />
              <button
                type="button"
                onClick={() => onToggle(spec.id, false)}
                data-testid={`service-remove-${spec.id}`}
                className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-muted font-semibold text-sm px-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none self-end"
              >
                Убрать услугу
              </button>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
